/**
 * AuthContext — exposes the current Firebase user + a loading flag.
 *
 * Wrap the app once at the root (in App.tsx) and use `useAuth()` anywhere.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { User } from "firebase/auth";

import { observeAuthState, consumeGoogleRedirect } from "../services/auth";
import { getProfile } from "../services/profiles";
import { initPushNotifications } from "../services/push";

type AuthState = {
  user: User | null;
  loading: boolean;
};

const AuthContext = createContext<AuthState>({ user: null, loading: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, loading: true });
  useEffect(() => {
    console.log('[BOOT] AuthProvider useEffect fired');
    const unsub = observeAuthState((user) => {
      console.log('[BOOT] auth callback fired, user:', user?.uid ?? 'null');
      setState({ user, loading: false });
      // If user has previously opted in to notifications, kick off push registration.
      if (user) {
        getProfile(user.uid)
          .then((p) => {
            if (p?.notificationsEnabled === true) {
              initPushNotifications(user.uid).catch((e) =>
                console.error('[BOOT] push init failed:', e),
              );
            }
          })
          .catch(() => undefined);
      }
    });
    console.log('[BOOT] observeAuthState returned, unsub type:', typeof unsub);
    return unsub;
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
